import { useContext } from "react";
import { useHistory } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import { useGetPaginatedAccountDocumentTemplatesQuery } from "../../../../generated/graphql";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Box } from "../../../Common/__styles__/Layout";
import { Button } from "../../../Common/Buttons/Button";
import Divider from "../../../Common/Divider";
import { EmptyState } from "../../../Common/EmptyState";
import { InsufficientPermissions } from "../../../Common/InsufficientPermissions";
import { useManualPaginationConfig } from "../../../Common/Tables/hooks";
import { Header, HeaderTitle, Section } from "../../__styles__/Content";
import Table from "./Table";

export const DocumentTemplates = () => {
  const history = useHistory();
  const { account, authorized } = useContext(AuthContext);
  const manualPaginationConfig = useManualPaginationConfig({ pageSize: 10 });

  const canListDocumentTemplates = authorized({
    resource: RESOURCE_NAME.DOCUMENT_TEMPLATE,
    permission: "list",
  });

  const canCreateDocumentTemplates = authorized({
    resource: RESOURCE_NAME.DOCUMENT_TEMPLATE,
    permission: "create",
  });

  const { data, error, loading, previousData, refetch } =
    useGetPaginatedAccountDocumentTemplatesQuery({
      variables: {
        accountId: account!.id,
        page: manualPaginationConfig.pagination.pageIndex + 1,
        pageSize: manualPaginationConfig.pagination.pageSize,
      },
      skip: !canListDocumentTemplates || !account?.id,
    });

  if (error) {
    throw new Error("Error loading document templates");
  }

  const documentTemplates = data?.getPaginatedAccountDocumentTemplates.data;
  const prevDocumentTemplates =
    previousData?.getPaginatedAccountDocumentTemplates.data;
  const hasDocumentTemplates = documentTemplates && documentTemplates.length;

  return (
    <Box>
      <Header>
        <HeaderTitle>Document template settings</HeaderTitle>
        {!loading && (
          <Button
            variant="outline"
            isDisabled={!canCreateDocumentTemplates}
            onPress={() =>
              history.push({ pathname: buildLink("createDocumentTemplate") })
            }
            size="small"
            iconStart="plus"
            title="Add template"
          />
        )}
      </Header>
      <Divider></Divider>
      {!canListDocumentTemplates && <InsufficientPermissions />}
      {canListDocumentTemplates &&
        !loading &&
        (hasDocumentTemplates ?
          <Section>
            <Table
              previousDocumentTemplates={prevDocumentTemplates ?? []}
              documentTemplates={documentTemplates}
              loading={loading}
              manualPaginationConfig={{
                ...manualPaginationConfig,
                pageCount:
                  data.getPaginatedAccountDocumentTemplates.pageInfo.totalPages,
              }}
              onUpdate={refetch}
            />
          </Section>
        : <EmptyState
            icon="add-file"
            title="No document templates for this account"
            body="Add a template to get started."
            paddingTop={spacing["2xl"].value}
            paddingBottom={spacing["2xl"].value}
          />)}
    </Box>
  );
};

export default DocumentTemplates;
